import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../auth';

/**
 * Onboarding v3 — checklist persistente (bolla SetupFloatingBubble).
 *
 * Stato salvato su `user_profiles.onboarding_state` (intake LLM, storico wizard
 * legacy) e `user_profiles.onboarding_checklist` (task della checklist).
 * Vedere migration `20260528_onboarding_v3.sql` per la forma dei payload.
 *
 * Pattern d'uso:
 * - Gli store/componenti CRM emettono `onboarding:task-completed` (via
 *   `dispatchTaskCompleted`) quando l'utente completa davvero un'azione.
 * - La riconciliazione (reconcileChecklist.ts) spunta i task già di fatto
 *   completati guardando i dati reali dell'org, una volta per sessione.
 * - Il wizard modale è stato rimosso (mai montato): StepKey/OnboardingState
 *   restano per compatibilità con i payload legacy in DB e per
 *   shouldShowOnboardingWizard, usato da Layout come flag "primo accesso".
 */

export type StepKey =
  | 'welcome'
  | 'company'
  | 'goals'
  | 'pipeline'
  | 'salesGoal'
  | 'done';

export type TaskKey =
  | 'profile'
  | 'pipeline'
  | 'goal'
  | 'integrations'
  | 'lead'
  | 'deals'
  | 'whatsapp'
  | 'automations'
  | 'team'
  | 'referral';

export interface OnboardingDataPayload {
  company?: string;
  vat?: string;
  sector?:
    | 'agency'
    | 'commerce'
    | 'tech'
    | 'pro'
    | 'edu'
    | 'health'
    | 'manuf'
    | 'other';
  size?: 'solo' | 'small' | 'med' | 'big';
  goals?: string[];
  pipeline?: 'b2b' | 'sales' | 'service' | 'custom';
  salesGoal?: number;
  [key: string]: unknown;
}

export interface OnboardingState {
  version: 3;
  started_at: string;
  completed_at: string | null;
  current_step: StepKey;
  skipped_steps: StepKey[];
  data: OnboardingDataPayload;
}

export interface ChecklistTaskState {
  done: boolean;
  done_at?: string;
}

export interface OnboardingChecklist {
  tasks: Record<TaskKey, ChecklistTaskState>;
  dismissed: boolean;
}

export interface UseOnboardingResult {
  state: OnboardingState | null;
  checklist: OnboardingChecklist | null;
  loading: boolean;
  error: string | null;
  // Mutators
  markTaskComplete: (task: TaskKey) => Promise<void>;
  /** Marca più task done in un unico UPDATE (usato dalla riconciliazione). */
  markTasksComplete: (tasks: TaskKey[]) => Promise<void>;
  dismissChecklist: () => Promise<void>;
  restoreChecklist: () => Promise<void>;
  resetOnboarding: () => Promise<void>;
  refetch: () => Promise<void>;
}

// L'hook è montato da più componenti contemporaneamente (bolla, layout,
// pagine): senza una cache condivisa ogni mount rifarebbe la stessa fetch
// su user_profiles al load della pagina.
type OnboardingRow = {
  onboarding_state: OnboardingState | null;
  onboarding_checklist: OnboardingChecklist | null;
};
const onboardingFetchCache = new Map<string, { promise: Promise<OnboardingRow>; ts: number }>();
const ONBOARDING_FETCH_TTL = 30 * 1000;

function loadOnboardingRow(userId: string, force = false): Promise<OnboardingRow> {
  const cached = onboardingFetchCache.get(userId);
  if (!force && cached && Date.now() - cached.ts < ONBOARDING_FETCH_TTL) {
    return cached.promise;
  }
  const promise = (async () => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('onboarding_state, onboarding_checklist')
      .eq('id', userId)
      .maybeSingle();
    if (error) throw error;
    return {
      onboarding_state: (data?.onboarding_state as OnboardingState | null) ?? null,
      onboarding_checklist: (data?.onboarding_checklist as OnboardingChecklist | null) ?? null,
    };
  })();
  onboardingFetchCache.set(userId, { promise, ts: Date.now() });
  promise.catch(() => onboardingFetchCache.delete(userId));
  return promise;
}

const DEFAULT_CHECKLIST: OnboardingChecklist = {
  tasks: {
    profile: { done: false },
    pipeline: { done: false },
    goal: { done: false },
    integrations: { done: false },
    lead: { done: false },
    deals: { done: false },
    whatsapp: { done: false },
    automations: { done: false },
    team: { done: false },
    referral: { done: false },
  },
  dismissed: false,
};

export function useOnboarding(): UseOnboardingResult {
  const { user } = useAuth();
  const [state, setState] = useState<OnboardingState | null>(null);
  const [checklist, setChecklist] = useState<OnboardingChecklist | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (force = false) => {
    if (!user) {
      setState(null);
      setChecklist(null);
      setLoading(false);
      return;
    }
    setError(null);
    try {
      const row = await loadOnboardingRow(user.id, force);
      setState(row.onboarding_state);
      setChecklist(row.onboarding_checklist);
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : String(fetchError));
    } finally {
      setLoading(false);
    }
  }, [user]);

  const refetch = useCallback(() => fetchData(true), [fetchData]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // Realtime sul proprio record user_profiles per propagare update
  // (es. wizard completato in un altro tab, checklist task spuntato da un evento).
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`onboarding-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          onboardingFetchCache.delete(user.id);
          const next = payload.new as {
            onboarding_state?: OnboardingState | null;
            onboarding_checklist?: OnboardingChecklist | null;
          };
          if ('onboarding_state' in next) {
            setState(next.onboarding_state ?? null);
          }
          if ('onboarding_checklist' in next) {
            setChecklist(next.onboarding_checklist ?? null);
          }
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user]);

  const persistState = useCallback(
    async (next: OnboardingState | null) => {
      if (!user) return;
      const { error: writeError } = await supabase
        .from('user_profiles')
        .update({ onboarding_state: next })
        .eq('id', user.id);
      if (writeError) {
        setError(writeError.message);
        throw writeError;
      }
      onboardingFetchCache.delete(user.id);
      setState(next);
    },
    [user],
  );

  const persistChecklist = useCallback(
    async (next: OnboardingChecklist | null) => {
      if (!user) return;
      const { error: writeError } = await supabase
        .from('user_profiles')
        .update({ onboarding_checklist: next })
        .eq('id', user.id);
      if (writeError) {
        setError(writeError.message);
        throw writeError;
      }
      onboardingFetchCache.delete(user.id);
      setChecklist(next);
    },
    [user],
  );

  const markTaskComplete = useCallback(
    async (task: TaskKey) => {
      const source = checklist ?? DEFAULT_CHECKLIST;
      if (source.tasks[task]?.done) return;
      const next: OnboardingChecklist = {
        ...source,
        tasks: {
          ...source.tasks,
          [task]: { done: true, done_at: new Date().toISOString() },
        },
      };
      await persistChecklist(next);
    },
    [checklist, persistChecklist],
  );

  const markTasksComplete = useCallback(
    async (tasks: TaskKey[]) => {
      const source = checklist ?? DEFAULT_CHECKLIST;
      const missing = tasks.filter((t) => !source.tasks[t]?.done);
      if (missing.length === 0) return;
      const now = new Date().toISOString();
      const next: OnboardingChecklist = {
        ...source,
        tasks: { ...source.tasks },
      };
      for (const t of missing) {
        next.tasks[t] = { done: true, done_at: now };
      }
      await persistChecklist(next);
    },
    [checklist, persistChecklist],
  );

  const dismissChecklist = useCallback(async () => {
    const source = checklist ?? DEFAULT_CHECKLIST;
    await persistChecklist({ ...source, dismissed: true });
  }, [checklist, persistChecklist]);

  const restoreChecklist = useCallback(async () => {
    const source = checklist ?? DEFAULT_CHECKLIST;
    await persistChecklist({ ...source, dismissed: false });
  }, [checklist, persistChecklist]);

  const resetOnboarding = useCallback(async () => {
    await persistState(null);
    await persistChecklist(null);
  }, [persistState, persistChecklist]);

  return {
    state,
    checklist,
    loading,
    error,
    markTaskComplete,
    markTasksComplete,
    dismissChecklist,
    restoreChecklist,
    resetOnboarding,
    refetch,
  };
}

/**
 * Helper read-only: decide se mostrare il wizard al primo accesso.
 * Lo usa Layout.tsx senza dover montare l'hook completo (latenza zero).
 */
export function shouldShowOnboardingWizard(
  onboardingState: OnboardingState | null | undefined,
): boolean {
  if (onboardingState === undefined) return false; // dati non ancora caricati
  if (onboardingState === null) return true; // mai avviato
  return onboardingState.completed_at === null; // avviato ma non finito
}
